import { FilterOperator, FilterType } from '@nbx/core/http';
import type { Criteria, Filter, Sort } from '@nbx/core/http';

import type { Amenity, AmenityCategory } from '@interfaces/amenity.interface';
import type { Booking } from '@interfaces/booking.interface';
import type { ContactPerson } from '@interfaces/contact.interface';
import type { Guest } from '@interfaces/guest.interface';
import type { Host } from '@interfaces/host.interface';
import type { City, Country, Location, Province } from '@interfaces/location.interface';
import {
  DeviceStatus,
  type LockAccessCode,
  type LockAccessCodesResponse,
  type LockAccessHistory,
  type LockActionResponse,
  type LockBattery,
  type LockDevice,
  type LockState,
  type LockStatus,
} from '@interfaces/lock.interface';
import type { MetricResponse } from '@interfaces/metric.interface';
import type { Rental } from '@interfaces/rental.interface';
import type { PagedResponse } from '@interfaces/response.interface';
import type { Season } from '@interfaces/seasons.interface';
import type { Space, SpaceType } from '@interfaces/space.interface';

import {
  type BookingStats,
  type MockBookingSpec,
  buildAccessMetrics,
  buildAccessMetricsOverallView,
  buildActivityOverviewFromStats,
  buildAllAmenityCategories,
  buildCities,
  buildContactPersons,
  buildCountries,
  buildGuests,
  buildHost,
  buildLocations,
  buildLockActionResponse,
  buildLockBattery,
  buildMockBookingsForTesting,
  buildPerformanceMetricsFromStats,
  buildProvinces,
  buildRental,
  buildSeasons,
  buildSpaceTypes,
  buildSpaces,
} from './builders';
import { RENTAL_TYPE_DATA } from './pools';

const host: Host & { externalCodeName: string } = {
  ...buildHost(),
  id: 'host-001',
  name: 'Mojacar Dream',
  externalCodeName: 'mojacardream',
  role: 'OWNER',
  paymentTimeoutEnabled: true,
  mirTimeoutEnabled: true,
};

const contacts: ContactPerson[] = buildContactPersons(3);
host.contactPersonId = contacts[0].id;

const countries: Country[] = buildCountries();
const provinces: Province[] = buildProvinces();
const cities: City[] = buildCities();
const locations: Location[] = buildLocations(3);

const spaceTypes: SpaceType[] = buildSpaceTypes();

const PROD_SPACE_TYPES = {
  Kitchen: { id: '660e8400-e29b-41d4-a716-446655440001', name: 'Cocina' },
  Bedroom: { id: '660e8400-e29b-41d4-a716-446655440002', name: 'Habitación' },
  LivingRoom: { id: '660e8400-e29b-41d4-a716-446655440003', name: 'Salón' },
  Bathroom: { id: '660e8400-e29b-41d4-a716-446655440004', name: 'Baño' },
  Others: { id: '660e8400-e29b-41d4-a716-446655440005', name: 'Otros' },
};

const BLOB = 'https://rdyproduploads.blob.core.windows.net/550e8400-e29b-41d4-a716-446655440009';
const img = (id: string, title: string, index: number) => ({
  id,
  url: `${BLOB}/${id}.webp`,
  title,
  description: title,
  index,
});

const spacesR0: Space[] = [
  {
    id: '313234e4-a7e4-4dbb-9bef-b6cd20244205',
    name: 'Salón',
    description:
      'Salón luminoso con sofá cómodo, TV de pantalla plana y mesa de comedor cerca de grandes ventanales. Detalles modernos y decoración cuidada que lo hacen acogedor y elegante.',
    typeId: PROD_SPACE_TYPES.LivingRoom.id,
    spaceType: PROD_SPACE_TYPES.LivingRoom,
    images: [
      img('8253752c-3fac-4488-95a0-dc5d3c5c5a58', 'Salón', 0),
      img('047b081d-db7a-4918-bd40-b018f7213742', 'Salón', 1),
      img('e4af6cc4-8093-4113-93a0-83dcd19bb515', 'Salón', 2),
    ],
  },
  {
    id: '8600bcb8-59ec-42b7-abbf-568ae116fa69',
    name: 'Habitación suite',
    description:
      'Dormitorio principal con cama de matrimonio, ropa de cama de calidad y armario amplio. Tonos neutros y ambiente tranquilo para un descanso reparador frente al mar.',
    typeId: PROD_SPACE_TYPES.Bedroom.id,
    spaceType: PROD_SPACE_TYPES.Bedroom,
    images: [
      img('ef8cba27-5640-4cbc-8ece-c9d5cb6acf8a', 'Habitación suite', 0),
      img('3ae025cf-4342-4ba1-9efc-f4118f631706', 'Habitación suite', 1),
      img('689d9acb-04d0-4c72-83e1-e4721cfaca94', 'Habitación suite', 2),
      img('bc491f1e-27ad-4420-a90e-7c4eedfd655b', 'Habitación suite', 3),
      img('6dcc3b01-cc33-427c-888a-4736ba733f7d', 'Habitación suite', 4),
      img('5ffe1f5e-7cd8-45df-82c3-fd49ceae61d3', 'Habitación suite', 5),
      img('c72d7a87-4ae5-4de4-a616-951d94fb2df3', 'Habitación suite', 6),
    ],
  },
  {
    id: '415530ff-56fd-4569-8c06-db90d3164ea8',
    name: 'Habitación',
    description:
      'Habitación acogedora con cama doble, cabecero blanco y decoración minimalista. Lámparas de lectura y textiles suaves que crean un ambiente cálido y perfecto para el descanso.',
    typeId: PROD_SPACE_TYPES.Bedroom.id,
    spaceType: PROD_SPACE_TYPES.Bedroom,
    images: [
      img('78a63f04-e9df-4eb8-81be-dc5c597b0950', 'Habitación', 0),
      img('cc2a77a2-41ee-4f92-a9a4-2aa6903587c3', 'Habitación', 1),
      img('e5d5d102-608e-4eaf-8707-008a86ff1fe1', 'Habitación', 2),
    ],
  },
  {
    id: '713cb61f-e2d2-4f6c-90d8-081f00ea4a0a',
    name: 'Baño suite',
    description:
      'Baño suite amplio con ducha de mampara de cristal, doble lavabo y espejos elegantes. Espacio moderno y funcional en tonos neutros para el dormitorio principal.',
    typeId: PROD_SPACE_TYPES.Bathroom.id,
    spaceType: PROD_SPACE_TYPES.Bathroom,
    images: [
      img('f85d5658-296a-4db4-8b54-bfcbbae258f8', 'Baño suite', 0),
      img('feec6171-bd4c-4407-86ce-f83ba547a5fd', 'Baño suite', 1),
      img('4b0527cb-bc5e-49d7-ad27-5ae524c0390a', 'Baño suite', 2),
      img('4722342f-bd6b-4937-b8a9-1ce6e21f1081', 'Baño suite', 3),
      img('4bd5d46e-d77b-4d90-999f-460f151af688', 'Baño suite', 4),
    ],
  },
  {
    id: '7d740c6b-44da-46f1-a422-ce04ccc9cf26',
    name: 'Baño',
    description:
      'Baño moderno con ducha acristalada, lavabo con mueble en verde oliva y espejo amplio. Detalles en madera que aportan calidez.',
    typeId: PROD_SPACE_TYPES.Bathroom.id,
    spaceType: PROD_SPACE_TYPES.Bathroom,
    images: [
      img('d89596de-8c24-4cb4-88f3-358b5be0366a', 'Baño', 0),
      img('263426d9-72f6-4fbe-a0f6-2d37b2b77d8c', 'Baño', 1),
      img('baaa1434-bd4f-4a96-8584-eb2bfd98e39c', 'Baño', 2),
    ],
  },
  {
    id: '3c457f3f-5261-484c-97ec-7ee210beeb53',
    name: 'Cocina',
    description:
      'Cocina luminosa con encimera azul y muebles blancos. Totalmente equipada con horno, vitrocerámica, microondas, frigorífico y menaje.',
    typeId: PROD_SPACE_TYPES.Kitchen.id,
    spaceType: PROD_SPACE_TYPES.Kitchen,
    images: [
      img('7818268d-7676-4428-9f96-ff7abe303254', 'Cocina', 0),
      img('a2bf97de-a081-4d1e-9c01-b2ef63f1a337', 'Cocina', 1),
      img('f6d27976-d68f-4177-a440-eb289d2f9387', 'Cocina', 2),
    ],
  },
  {
    id: '36922145-667e-4e7c-bb48-44c987cad2f1',
    name: 'Balcón',
    description:
      'Balcón con zona de estar y comedor exterior con vistas frontales al mar, a la Torre de Macenas y a la línea costera del levante almeriense.',
    typeId: PROD_SPACE_TYPES.Others.id,
    spaceType: PROD_SPACE_TYPES.Others,
    images: [
      img('0db5d72a-7874-4c9e-af68-aae795abf388', 'Balcón', 0),
      img('3b7fba42-1f11-4ad1-a47c-0e4d4435dc81', 'Balcón', 1),
      img('b2df6c0b-9bca-437e-a8ef-1078b78b0e3a', 'Balcón', 2),
      img('7a6f1cb2-ab91-4613-8499-5328cb1d5c65', 'Balcón', 3),
      img('31c9321b-7d73-4ef5-bf93-b04472fb32e0', 'Balcón', 4),
      img('ae3f474f-34c5-4541-989c-f997b706b9cc', 'Balcón', 5),
    ],
  },
  {
    id: '179b946f-7227-4001-a64a-c9c551390169',
    name: 'Exterior, zonas comunes e instalaciones',
    description:
      'Complejo con acceso directo a la playa, 2 piscinas gratuitas, zonas ajardinadas, club social, gimnasio, pistas deportivas y parque infantil.',
    typeId: PROD_SPACE_TYPES.Others.id,
    spaceType: PROD_SPACE_TYPES.Others,
    images: [
      img('04c7e171-a520-4ef8-b682-06b728a7a7ba', 'Exterior', 0),
      img('4f9b0147-1c2c-4872-b1eb-b8f7ef2b0465', 'Exterior', 1),
      img('2e09e310-4015-4db8-a486-111d36cae08a', 'Exterior', 2),
      img('147a0e3d-98d1-4402-a9c2-90e25612dfc7', 'Exterior', 3),
      img('f22a3874-d49c-4bae-882a-120e1e323844', 'Exterior', 4),
      img('a4b33486-21f9-474d-9e93-7843e52c2a99', 'Exterior', 5),
      img('5dba08cc-eb62-4ba1-89eb-5c318fe9d7c4', 'Exterior', 6),
    ],
  },
];

const spacesR1: Space[] = [
  {
    id: '7b3107e7-dd4e-4c71-9ac8-0a285422013a',
    name: 'Salón',
    description:
      'Salón amplio y luminoso con sofá, TV de pantalla plana y mesa de comedor junto a ventanales. Decoración moderna y ambiente elegante y acogedor.',
    typeId: PROD_SPACE_TYPES.LivingRoom.id,
    spaceType: PROD_SPACE_TYPES.LivingRoom,
    images: [
      img('4c8c0a09-77dd-4cea-b314-d2db16ab33cc', 'Salón', 0),
      img('a01ccb91-084d-421d-8225-50d957960732', 'Salón', 1),
      img('291c839a-aa44-447c-a365-23b88f3dffea', 'Salón', 2),
      img('684b309b-7d01-4f47-bf0c-8e6b6bbb30bd', 'Salón', 3),
      img('c807adda-5747-4de9-8048-c70241065d34', 'Salón', 4),
    ],
  },
];

const spacesGeneric: Space[] = buildSpaces(6, spaceTypes);

const spaces: Space[] = [...spacesR0];

const spacesByRentalIdMap: Record<string, Space[]> = {
  'rental-mojacar-001': spacesR0,
  'rental-mojacar-002': spacesR1,
  'rental-mojacar-003': spacesGeneric.slice(0, 3),
  'rental-mojacar-004': spacesGeneric.slice(3, 6),
};

const amenityCategories: AmenityCategory[] = buildAllAmenityCategories();

const MOJACAR_CITY = { id: 99, name: 'Mojácar' };
const MOJACAR_COUNTRY = { id: 'ES', name: 'España' };

const MOJACAR_RENTAL_TYPE_APARTMENT = {
  id: RENTAL_TYPE_DATA[0].id,
  name: RENTAL_TYPE_DATA[0].name,
};
const MOJACAR_RENTAL_TYPE_HOUSE = {
  id: RENTAL_TYPE_DATA[1]?.id ?? RENTAL_TYPE_DATA[0].id,
  name: RENTAL_TYPE_DATA[1]?.name ?? RENTAL_TYPE_DATA[0].name,
};

const rentals: Rental[] = [
  buildRental({
    id: 'rental-mojacar-001',
    title: 'Casa Mar de Mojácar',
    description:
      'Apartamento en Macenas Resort con 2 habitaciones, 2 baños, salón, cocina, terraza y patio con vistas frontales al mar. Urbanización vigilada con 2 piscinas, acceso directo a la playa, club social, parking privado, supermercado y zonas infantiles.',
    shortDescription: 'Frente al Mar: Vistas Increíbles',
    defaultPrice: 145,
    rating: 4.55,
    maxGuests: 6,
    bedrooms: 2,
    bathrooms: 2,
    area: 70,
    minStay: 2,
    maxStay: 14,
    instantBooking: true,
    isDraft: false,
    hasPublished: true,
    rentalType: MOJACAR_RENTAL_TYPE_APARTMENT,
    locationText:
      'El alojamiento está en Macenas Resort, a cinco minutos a pie de la playa, con supermercado y club social dentro de la urbanización.',
    faqs: [
      {
        id: 'faq-mojacar-parking',
        question: '¿Hay aparcamiento en la vivienda?',
        answer: 'Sí, dispone de una plaza de garaje privada incluida en la reserva.',
        sortOrder: 0,
        enabled: true,
      },
      {
        id: 'faq-mojacar-pool',
        question: '¿La urbanización tiene piscina?',
        answer: 'Sí, hay dos piscinas comunitarias de uso exclusivo para residentes y huéspedes.',
        sortOrder: 1,
        enabled: true,
      },
      {
        id: 'faq-mojacar-pets',
        question: '¿Se admiten mascotas?',
        answer: 'No se admiten mascotas en este alojamiento.',
        sortOrder: 2,
        enabled: false,
      },
    ],
    location: {
      address: 'Macenas',
      city: MOJACAR_CITY,
      country: MOJACAR_COUNTRY,
      zipCode: '04638',
      marker: { latitude: '37.0815262', longitude: '-1.8527202' },
      map: { latitude: '37.0815262', longitude: '-1.8527202' },
    },
    mainImage: {
      id: '7a6f1cb2-ab91-4613-8499-5328cb1d5c65',
      url: `${BLOB}/7a6f1cb2-ab91-4613-8499-5328cb1d5c65.webp`,
    },
    requiresLock: true,
    lock: {
      ragId: 'rag-mojacar-bridge-001',
      ragLoginUser: 'lock-user-mojacar',
      ragLoginPass: null,
      ragMainDeviceId: 'device-mojacar-main-001',
      canLock: true,
    },
  }),

  buildRental({
    id: 'rental-mojacar-002',
    title: 'Ático Mirador del Cantal',
    description:
      'Apartamento en Macenas Resort con 3 dormitorios, 2 baños, salón, cocina, terraza y patio con vistas directas al mar. Urbanización privada con vigilancia, 2 piscinas, acceso a la playa, club social, parking cubierto, supermercado y áreas infantiles.',
    shortDescription: 'Mirador al Mediterráneo',
    defaultPrice: 140,
    rating: 4.73,
    maxGuests: 6,
    bedrooms: 3,
    bathrooms: 2,
    area: 100,
    minStay: 3,
    maxStay: 21,
    instantBooking: false,
    isDraft: false,
    hasPublished: true,
    rentalType: MOJACAR_RENTAL_TYPE_APARTMENT,
    location: {
      address: 'Calle Califato 5',
      city: MOJACAR_CITY,
      country: MOJACAR_COUNTRY,
      zipCode: '04638',
      marker: { latitude: '37.0814776', longitude: '-1.8536332' },
      map: { latitude: '37.0814776', longitude: '-1.8536332' },
    },
    mainImage: {
      id: 'c807adda-5747-4de9-8048-c70241065d34',
      url: `${BLOB}/c807adda-5747-4de9-8048-c70241065d34.webp`,
    },
    lock: {
      ragId: 'rag-cantal-bridge-001',
      ragLoginUser: 'lock-user-cantal',
      ragLoginPass: null,
      ragMainDeviceId: 'device-cantal-main-001',
      canLock: true,
    },
  }),

  buildRental({
    id: 'rental-mojacar-003',
    title: 'Loft Piedra Villazar',
    description:
      'Encantador loft de piedra en el casco histórico de Mojácar Pueblo. Reformado con gusto respetando la arquitectura tradicional andaluza. Ambiente íntimo y auténtico a pocos metros de la fuente mora.',
    shortDescription: 'Loft de piedra en el casco histórico.',
    defaultPrice: 85,
    rating: 4.6,
    maxGuests: 2,
    bedrooms: 1,
    bathrooms: 1,
    area: 55,
    minStay: 1,
    maxStay: 0,
    instantBooking: true,
    isDraft: false,
    hasPublished: true,
    rentalType: MOJACAR_RENTAL_TYPE_APARTMENT,
    location: {
      address: 'Calle Enmedio, 12',
      city: MOJACAR_CITY,
      country: MOJACAR_COUNTRY,
      zipCode: '04638',
      marker: { latitude: '37.1515', longitude: '-1.8493' },
      map: { latitude: '37.1515', longitude: '-1.8493' },
    },
    mainImage: {
      id: '04c7e171-a520-4ef8-b682-06b728a7a7ba',
      url: `${BLOB}/04c7e171-a520-4ef8-b682-06b728a7a7ba.webp`,
    },
    lock: {
      ragId: 'rag-villazar-bridge-001',
      ragLoginUser: 'lock-user-villazar',
      ragLoginPass: null,
      ragMainDeviceId: 'device-villazar-main-001',
      canLock: false,
    },
  }),

  buildRental({
    id: 'rental-mojacar-004',
    title: 'Casa Cuesta de la Fuente',
    description:
      'Típica casa morisca en la cuesta de la fuente, con patio interior, vistas al pueblo blanco y al mar al fondo. Perfecta para descubrir el encanto de Mojácar Pueblo con comodidades modernas.',
    shortDescription: 'Casa morisca con vistas al pueblo y al mar.',
    defaultPrice: 110,
    rating: 4.5,
    maxGuests: 4,
    bedrooms: 2,
    bathrooms: 1,
    area: 80,
    minStay: 0,
    maxStay: 30,
    instantBooking: false,
    isDraft: false,
    hasPublished: true,
    rentalType: MOJACAR_RENTAL_TYPE_HOUSE,
    location: {
      address: 'Cuesta de la Fuente, 3',
      city: MOJACAR_CITY,
      country: MOJACAR_COUNTRY,
      zipCode: '04638',
      marker: { latitude: '37.1528', longitude: '-1.8507' },
      map: { latitude: '37.1528', longitude: '-1.8507' },
    },
    mainImage: {
      id: '5dba08cc-eb62-4ba1-89eb-5c318fe9d7c4',
      url: `${BLOB}/5dba08cc-eb62-4ba1-89eb-5c318fe9d7c4.webp`,
    },
    lock: {
      ragId: 'rag-fuente-bridge-001',
      ragLoginUser: 'lock-user-fuente',
      ragLoginPass: null,
      ragMainDeviceId: 'device-fuente-main-001',
      canLock: false,
    },
  }),
].map(r => ({
  ...r,
  hostId: host.id,
  hostExternalCodeName: host.externalCodeName,
}));

const enrichBooking = (b: Booking) => ({
  ...b,
  rentalTitle: rentals.find(r => r.id === b.rentalId)?.title,
  rentalMainImageUrl: rentals.find(r => r.id === b.rentalId)?.mainImage?.url,
});

const casaMarSpecs: MockBookingSpec[] = [
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-02-02',
    checkOut: '2026-02-06',
    status: 'FINISHED',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-02-15',
    checkOut: '2026-02-21',
    status: 'FINISHED',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-02-23',
    checkOut: '2026-03-02',
    status: 'FINISHED',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-03-03',
    checkOut: '2026-03-11',
    status: 'FINISHED',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-03-11',
    checkOut: '2026-03-13',
    status: 'FINISHED',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-03-16',
    checkOut: '2026-03-19',
    status: 'FINISHED',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-03-29',
    checkOut: '2026-04-03',
    status: 'FINISHED',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-04-14',
    checkOut: '2026-04-21',
    status: 'FINISHED',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-04-27',
    checkOut: '2026-04-29',
    status: 'FINISHED',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-05-05',
    checkOut: '2026-05-08',
    status: 'FINISHED',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-05-10',
    checkOut: '2026-05-14',
    status: 'FINISHED',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-05-18',
    checkOut: '2026-05-23',
    status: 'CHECKOUT_PENDING',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-05-26',
    checkOut: '2026-06-01',
    status: 'CHECKIN_PENDING',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-09-07',
    checkOut: '2026-09-12',
    status: 'FINISHED',
    channel: 'BDC',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-09-21',
    checkOut: '2026-09-26',
    status: 'GUEST_MIR_DATA_PENDING',
    channel: 'Booking.com',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-10-05',
    checkOut: '2026-10-09',
    status: 'CHECKIN_PENDING',
    channel: 'Expedia',
  },
];

/**
 * One stay for each way a channel arrives, in the states the backend walks it through today. A
 * channel import enters GUEST_MIR_DATA_PENDING exactly like a booking made on our own site, so only
 * `channel` tells them apart: Airbnb in both spellings, the ABB code as Channex sends it, a code
 * Channex could not resolve, an empty channel and our own site. Kept out of the Casa Mar figures
 * below.
 */
export const channelBookingSpecs: MockBookingSpec[] = [
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-10-14',
    checkOut: '2026-10-18',
    status: 'GUEST_MIR_DATA_PENDING',
    channel: 'Airbnb',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-10-21',
    checkOut: '2026-10-24',
    status: 'BOOKING_CANCELLED',
    channel: 'ABB',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-11-02',
    checkOut: '2026-11-05',
    status: 'GUEST_MIR_DATA_PENDING',
    channel: 'AIRBNB',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-11-09',
    checkOut: '2026-11-12',
    status: 'GUEST_MIR_DATA_PENDING',
    channel: 'UNKNOWN',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-11-16',
    checkOut: '2026-11-19',
    status: 'GUEST_MIR_DATA_PENDING',
    channel: '',
  },
  {
    rentalId: 'rental-mojacar-001',
    checkIn: '2026-11-23',
    checkOut: '2026-11-26',
    status: 'GUEST_MIR_DATA_PENDING',
    channel: 'SSR',
  },
];

const bookings: Booking[] = buildMockBookingsForTesting(
  rentals.slice(1).map(r => r.id),
  host.id,
  [...casaMarSpecs, ...channelBookingSpecs]
).map(enrichBooking);

function _nightsInWindow(ci: string, co: string, start: Date, end: Date): number {
  const a = new Date(ci) < start ? start : new Date(ci);
  const b = new Date(co) > end ? end : new Date(co);
  return Math.max(0, (b.getTime() - a.getTime()) / 86_400_000);
}

const _today = new Date();
const _lm0 = new Date(_today);
_lm0.setDate(_today.getDate() - 30);
const _pm0 = new Date(_today);
_pm0.setDate(_today.getDate() - 60);
const _lw0 = new Date(_today);
_lw0.setDate(_today.getDate() - 7);
const _pw0 = new Date(_today);
_pw0.setDate(_today.getDate() - 14);
const _AVG_PRICE = 102;

const _nightsLM = casaMarSpecs.reduce(
  (s, b) => s + _nightsInWindow(b.checkIn, b.checkOut, _lm0, _today),
  0
);
const _nightsPM = casaMarSpecs.reduce(
  (s, b) => s + _nightsInWindow(b.checkIn, b.checkOut, _pm0, _lm0),
  0
);
const _occLM = Math.round((_nightsLM / 30) * 100);
const _occPM = Math.round((_nightsPM / 30) * 100);
const _incomeLM = Math.round(_nightsLM * _AVG_PRICE);
const _incomePM = Math.round(_nightsPM * _AVG_PRICE);
const _bkgsLW = casaMarSpecs.filter(
  s => new Date(s.checkIn) >= _lw0 && new Date(s.checkIn) < _today
).length;
const _bkgsPW = casaMarSpecs.filter(
  s => new Date(s.checkIn) >= _pw0 && new Date(s.checkIn) < _lw0
).length;
const _totalNights = casaMarSpecs.reduce(
  (s, b) => s + (new Date(b.checkOut).getTime() - new Date(b.checkIn).getTime()) / 86_400_000,
  0
);

const _casaMarStats: BookingStats = {
  totalBookingsLastWeek: _bkgsLW,
  bookingsWeekChange: _bkgsLW - _bkgsPW,
  cancellationsLastWeek: 0,
  occupancyRateLastMonth: _occLM,
  occupancyChange: _occLM - _occPM,
  incomeLastMonth: _incomeLM,
  incomeChangePercent: _incomePM > 0 ? Math.round(((_incomeLM - _incomePM) / _incomePM) * 100) : 0,
  revenuePerNight: _AVG_PRICE,
  cancellationRatePercent: 0,
  totalRevenue: Math.round(_totalNights * _AVG_PRICE),
  totalBookings: casaMarSpecs.length,
  averageRating: 4.55,
};

const guests: Guest[] = buildGuests(12);

const lockDevices: LockDevice[] = [
  {
    deviceId: 'device-mojacar-main-001',
    name: 'Puerta de Entrada',
    batteryLevel: 82,
    status: DeviceStatus.ONLINE,
    type: 'smart',
    isMainDevice: true,
    createdAt: '2024-09-15T10:30:00.000Z',
    pairedAt: '2024-09-15T10:45:00.000Z',
    updatedAt: '2025-05-01T08:00:00.000Z',
    rssi: -52,
    unlockDirection: 'right',
  },
  {
    deviceId: 'device-mojacar-terrace-001',
    name: 'Puerta Terraza',
    batteryLevel: 67,
    status: DeviceStatus.ONLINE,
    type: 'smart',
    isMainDevice: false,
    createdAt: '2024-09-15T11:00:00.000Z',
    pairedAt: '2024-09-15T11:15:00.000Z',
    updatedAt: '2025-05-01T08:00:00.000Z',
    rssi: -61,
    unlockDirection: 'left',
  },
];

const lockDevicesCantal: LockDevice[] = [
  {
    deviceId: 'device-cantal-main-001',
    name: 'Puerta Principal',
    batteryLevel: 91,
    status: DeviceStatus.ONLINE,
    type: 'smart',
    isMainDevice: true,
    createdAt: '2024-10-05T09:00:00.000Z',
    pairedAt: '2024-10-05T09:15:00.000Z',
    updatedAt: '2025-05-01T08:00:00.000Z',
    rssi: -48,
    unlockDirection: 'right',
  },
  {
    deviceId: 'device-cantal-parking-001',
    name: 'Garaje Cubierto',
    batteryLevel: 55,
    status: DeviceStatus.ONLINE,
    type: 'smart',
    isMainDevice: false,
    createdAt: '2024-10-05T09:30:00.000Z',
    pairedAt: '2024-10-05T09:45:00.000Z',
    updatedAt: '2025-05-01T08:00:00.000Z',
    rssi: -67,
    unlockDirection: 'left',
  },
];

const lockDevicesVillazar: LockDevice[] = [
  {
    deviceId: 'device-villazar-main-001',
    name: 'Puerta de Entrada',
    batteryLevel: 38,
    status: DeviceStatus.ONLINE,
    type: 'smart',
    isMainDevice: true,
    createdAt: '2025-01-20T10:00:00.000Z',
    pairedAt: '2025-01-20T10:10:00.000Z',
    updatedAt: '2025-05-01T08:00:00.000Z',
    rssi: -59,
    unlockDirection: 'right',
  },
];

const lockDevicesFuente: LockDevice[] = [
  {
    deviceId: 'device-fuente-main-001',
    name: 'Puerta de Entrada',
    batteryLevel: 74,
    status: DeviceStatus.ONLINE,
    type: 'smart',
    isMainDevice: true,
    createdAt: '2024-11-10T09:00:00.000Z',
    pairedAt: '2024-11-10T09:20:00.000Z',
    updatedAt: '2025-05-01T08:00:00.000Z',
    rssi: -55,
    unlockDirection: 'right',
  },
];

const accessCodes: LockAccessCode[] = [
  {
    codeName: 'Carlos Pérez — Reserva May 2026',
    codeType: 'Automatic' as any,
    startDate: '2026-05-15',
    endDate: '2026-05-22',
    passcode: '4872',
    status: 'ACTIVE' as any,
  },
  {
    codeName: 'Emma García — Limpieza',
    codeType: 'Manual' as any,
    startDate: '2026-05-01',
    endDate: '2026-06-30',
    passcode: '1930',
    status: 'ACTIVE' as any,
  },
  {
    codeName: 'Diego López — Reserva Jun 2026',
    codeType: 'Automatic' as any,
    startDate: '2026-06-12',
    endDate: '2026-06-19',
    passcode: '7215',
    status: 'ACTIVE' as any,
  },
  {
    codeName: 'Ana Martínez — Reserva Abr 2026',
    codeType: 'Automatic' as any,
    startDate: '2026-04-08',
    endDate: '2026-04-15',
    passcode: '3641',
    status: 'EXPIRED' as any,
  },
  {
    codeName: 'Mantenimiento — Revisión técnica',
    codeType: 'Manual' as any,
    startDate: '2026-03-10',
    endDate: '2026-03-11',
    passcode: '5509',
    status: 'EXPIRED' as any,
  },
  {
    codeName: '',
    codeType: 'Manual' as any,
    startDate: '1999-12-31T23:00:00',
    endDate: '2027-01-15T10:00:00',
    passcode: '2416',
    status: 'ACTIVE' as any,
  },
  {
    codeName: '',
    codeType: 'Manual' as any,
    startDate: '1999-12-31T23:00:00',
    endDate: '2026-03-05T10:00:00',
    passcode: '6132',
    status: 'EXPIRED' as any,
  },
];

const accessHistory: LockAccessHistory[] = [
  {
    access: '4872',
    accessMethod: 'PASSCODE',
    status: 'SUCCESS',
    statusCode: 200,
    activity: 'UNLOCK',
    date: '2026-05-15',
    time: '15:42',
  },
  {
    access: '4872',
    accessMethod: 'PASSCODE',
    status: 'SUCCESS',
    statusCode: 200,
    activity: 'LOCK',
    date: '2026-05-15',
    time: '16:05',
  },
  {
    access: '1930',
    accessMethod: 'PASSCODE',
    status: 'SUCCESS',
    statusCode: 200,
    activity: 'UNLOCK',
    date: '2026-05-16',
    time: '10:30',
  },
  {
    access: '1930',
    accessMethod: 'PASSCODE',
    status: 'SUCCESS',
    statusCode: 200,
    activity: 'LOCK',
    date: '2026-05-16',
    time: '11:15',
  },
  {
    access: '4872',
    accessMethod: 'PASSCODE',
    status: 'SUCCESS',
    statusCode: 200,
    activity: 'UNLOCK',
    date: '2026-05-18',
    time: '22:10',
  },
  {
    access: '4872',
    accessMethod: 'PASSCODE',
    status: 'FAILED',
    statusCode: 401,
    activity: 'UNLOCK',
    date: '2026-05-19',
    time: '08:03',
  },
  {
    access: '4872',
    accessMethod: 'PASSCODE',
    status: 'SUCCESS',
    statusCode: 200,
    activity: 'UNLOCK',
    date: '2026-05-19',
    time: '08:05',
  },
  {
    access: '4872',
    accessMethod: 'PASSCODE',
    status: 'SUCCESS',
    statusCode: 200,
    activity: 'LOCK',
    date: '2026-05-22',
    time: '10:48',
  },
  {
    access: '3641',
    accessMethod: 'PASSCODE',
    status: 'SUCCESS',
    statusCode: 200,
    activity: 'UNLOCK',
    date: '2026-04-08',
    time: '15:05',
  },
  {
    access: '3641',
    accessMethod: 'PASSCODE',
    status: 'SUCCESS',
    statusCode: 200,
    activity: 'LOCK',
    date: '2026-04-14',
    time: '11:00',
  },
];

const rentalTypes = RENTAL_TYPE_DATA.map(rt => ({ ...rt }));
const seasons: Season[] = buildSeasons();

const HOST_NAMES: Record<string, string> = {
  mojacardream: 'Mojacar Dream',
  costablanca: 'Costa Blanca Rentals',
  sierranevada: 'Sierra Nevada Lodge',
};

interface LegacyRentalFilters {
  isDraft?: boolean;
  searchValue?: string;
  type?: string;
  page?: number;
  size?: number;
}

interface LegacyBookingFilters {
  rentalId?: string;
  statuses?: string[];
  bookingStatus?: string;
  searchValue?: string;
  checkInDate?: string;
  checkOutDate?: string;
  page?: number;
  size?: number;
}

type MockSort = Sort & { field?: string; direction?: 'asc' | 'desc' };
type FieldResolver<T> = (item: T, filter: Pick<Filter, 'field' | 'value'>) => unknown;

function paginate<T>(items: T[], page = 0, size = 20, sorted = false): PagedResponse<T> {
  const safePage = Number.isFinite(page) ? Math.max(page, 0) : 0;
  const safeSize = Number.isFinite(size) ? Math.max(size, 1) : 20;
  const start = safePage * safeSize;
  const content = items.slice(start, start + safeSize);
  const totalPages = Math.ceil(items.length / safeSize);
  return {
    content,
    totalElements: items.length,
    totalPages,
    size: safeSize,
    page: safePage,
    sorted,
    totalElementsPage: content.length,
    last: safePage >= Math.max(totalPages - 1, 0),
  };
}

function isCriteria(value: unknown): value is Criteria {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Criteria;
  return Array.isArray(candidate.filters) || Array.isArray(candidate.sorts);
}

function getPathValue(source: unknown, path: string): unknown {
  if (!source || typeof source !== 'object') {
    return undefined;
  }

  const record = source as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(record, path)) {
    return record[path];
  }

  return path.split('.').reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') {
      return undefined;
    }
    return (current as Record<string, unknown>)[key];
  }, source);
}

function getAliasedValue<T>(
  item: T,
  field: string,
  aliases: Record<string, string[]> = {}
): unknown {
  const paths = aliases[field] ?? [field];
  for (const path of paths) {
    const value = getPathValue(item, path);
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

function compareValues(left: unknown, right: unknown): number {
  const leftComparable = toComparable(left);
  const rightComparable = toComparable(right);

  if (leftComparable == null && rightComparable == null) {
    return 0;
  }
  if (leftComparable == null) {
    return -1;
  }
  if (rightComparable == null) {
    return 1;
  }
  if (leftComparable === rightComparable) {
    return 0;
  }
  return leftComparable > rightComparable ? 1 : -1;
}

function toComparable(value: unknown): string | number | boolean | null {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    const timestamp = Date.parse(trimmed);
    return Number.isNaN(timestamp) ? trimmed.toLowerCase() : timestamp;
  }
  return value == null ? null : String(value as string | number | boolean).toLowerCase();
}

function valuesEqual(left: unknown, right: unknown): boolean {
  if (typeof left === 'number' && typeof right === 'string') {
    const numericRight = Number(right);
    return !Number.isNaN(numericRight) && left === numericRight;
  }
  if (typeof left === 'string' && typeof right === 'number') {
    const numericLeft = Number(left);
    return !Number.isNaN(numericLeft) && numericLeft === right;
  }
  return compareValues(left, right) === 0;
}

function includesValue(source: unknown, expected: unknown): boolean {
  if (source == null || expected == null) {
    return false;
  }
  if (Array.isArray(source)) {
    return source.some(item => valuesEqual(item, expected));
  }
  return String(source as string | number | boolean)
    .toLowerCase()
    .includes(String(expected as string | number | boolean).toLowerCase());
}

function matchesFilter<T>(item: T, filter: Filter, resolveField: FieldResolver<T>): boolean {
  const actualValue = resolveField(item, filter);
  const expectedValue = filter.value;

  if (actualValue === undefined) {
    return (
      filter.operator === FilterOperator.NOT_EQUAL ||
      filter.operator === FilterOperator.NOT_CONTAINS
    );
  }

  switch (filter.operator) {
    case FilterOperator.EQUAL:
      return valuesEqual(actualValue, expectedValue);
    case FilterOperator.NOT_EQUAL:
      return !valuesEqual(actualValue, expectedValue);
    case FilterOperator.CONTAINS:
      return includesValue(actualValue, expectedValue);
    case FilterOperator.NOT_CONTAINS:
      return !includesValue(actualValue, expectedValue);
    case FilterOperator.GT:
      return compareValues(actualValue, expectedValue) > 0;
    case FilterOperator.GTE:
      return compareValues(actualValue, expectedValue) >= 0;
    case FilterOperator.LT:
      return compareValues(actualValue, expectedValue) < 0;
    case FilterOperator.LTE:
      return compareValues(actualValue, expectedValue) <= 0;
    case FilterOperator.IN: {
      const expectedValues = Array.isArray(expectedValue) ? expectedValue : [expectedValue];
      if (Array.isArray(actualValue)) {
        return actualValue.some(value =>
          expectedValues.some(expected => valuesEqual(value, expected))
        );
      }
      return expectedValues.some(expected => valuesEqual(actualValue, expected));
    }
    default:
      return true;
  }
}

function applyCriteria<T>(
  items: T[],
  criteria: Criteria | undefined,
  resolveField: FieldResolver<T>
): T[] {
  const filters =
    criteria?.filters?.filter(filter => filter.field && filter.operator && filter.value != null) ??
    [];
  const andFilters = filters.filter(filter => filter.type !== FilterType.OR);
  const orFilters = filters.filter(filter => filter.type === FilterType.OR);

  const filtered = items.filter(item => {
    const matchesAnd = andFilters.every(filter => matchesFilter(item, filter, resolveField));
    const matchesOr =
      orFilters.length === 0 || orFilters.some(filter => matchesFilter(item, filter, resolveField));
    return matchesAnd && matchesOr;
  });

  return applySorts(filtered, criteria?.sorts, resolveField);
}

function applySorts<T>(items: T[], sorts: Sort[] | undefined, resolveField: FieldResolver<T>): T[] {
  if (!sorts?.length) {
    return items;
  }

  return [...items].sort((left, right) => {
    for (const sort of sorts as MockSort[]) {
      const sortField = sort.sortField ?? sort.field;
      if (!sortField) {
        continue;
      }
      const sortType = (sort.sortType ?? sort.direction ?? 'asc').toLowerCase();
      const direction = sortType === 'desc' ? -1 : 1;
      const comparison = compareValues(
        resolveField(left, { field: sortField, value: undefined }),
        resolveField(right, { field: sortField, value: undefined })
      );
      if (comparison !== 0) {
        return comparison * direction;
      }
    }
    return 0;
  });
}

const rentalAliases: Record<string, string[]> = {
  name: ['title'],
  rentalTypeId: ['rentalTypeId', 'rentalType.id'],
  'rentalType.id': ['rentalType.id'],
};

function resolveRentalField(rental: Rental, filter: Pick<Filter, 'field' | 'value'>): unknown {
  if (filter.field === 'status') {
    return rental.isDraft ? 'DRAFT' : 'PUBLISHED';
  }
  return getAliasedValue(rental, filter.field, rentalAliases);
}

const bookingAliases: Record<string, string[]> = {
  checkInDate: ['checkInDate', 'time.checkInDate'],
  checkOutDate: ['checkOutDate', 'time.checkOutDate'],
  bookingHolderName: ['bookingHolder.name'],
  mainGuestName: ['mainGuest.name'],
};

/** The backend's effective channel: the OTA code, else the booking's own `channel` (`SSR`). */
function effectiveBookingChannel(booking: Booking): string {
  const code = booking.sourceChannel?.trim();
  const candidate = code && code.toUpperCase() !== 'UNKNOWN' ? code : (booking.channel ?? '');
  const alias = candidate.trim().toLowerCase();
  if (['airbnb', 'abb'].includes(alias)) {
    return 'ABB';
  }
  if (['booking', 'booking.com', 'bookingcom', 'bdc'].includes(alias)) {
    return 'BDC';
  }
  return candidate || 'UNKNOWN';
}

function resolveBookingField(booking: Booking, filter: Pick<Filter, 'field' | 'value'>): unknown {
  if (filter.field === 'sourceChannel') {
    return effectiveBookingChannel(booking);
  }
  return getAliasedValue(booking, filter.field, bookingAliases);
}

export const mockDb = {
  host,
  contacts,
  countries,
  provinces,
  cities,
  locations,
  spaceTypes,
  spaces,
  amenityCategories,
  rentals,
  bookings,
  guests,
  lockDevices,
  accessCodes,
  accessHistory,
  rentalTypes,
  seasons,
  hostNames: HOST_NAMES,

  getHost(): Host & { externalCodeName: string } {
    return host;
  },

  getHostNames(): Record<string, string> {
    return HOST_NAMES;
  },

  getHostNameByCode(code: string): { name: string } | null {
    const name = HOST_NAMES[code];
    return name ? { name } : null;
  },

  getContactPersons(): ContactPerson[] {
    return contacts;
  },

  getContactPersonById(id: string): ContactPerson | undefined {
    return contacts.find(c => c.id === id);
  },

  getRentalById(id: string): Rental | undefined {
    return rentals.find(r => r.id === id);
  },

  searchRentals(filters: Criteria | LegacyRentalFilters = {}): PagedResponse<Rental> {
    if (isCriteria(filters)) {
      const result = applyCriteria(rentals, filters, resolveRentalField);
      return paginate(result, filters.page, filters.size, !!filters.sorts?.length);
    }

    let result = [...rentals];

    if (filters.isDraft !== undefined) {
      result = result.filter(r => r.isDraft === filters.isDraft);
    }

    if (filters.searchValue) {
      const s = filters.searchValue.toLowerCase();
      result = result.filter(
        r => r.title?.toLowerCase().includes(s) || r.description?.toLowerCase().includes(s)
      );
    }

    if (filters.type) {
      result = result.filter(r => r.rentalType?.id === filters.type);
    }

    return paginate(result, filters.page, filters.size);
  },

  getAllRentals(page = 0, size = 20): PagedResponse<Rental> {
    return paginate(rentals, page, size);
  },

  getRentalTypes() {
    return rentalTypes;
  },

  getSeasons(): Season[] {
    return seasons;
  },

  getSeasonById(id: string): Season | undefined {
    return seasons.find(s => s.id === id);
  },

  addSeason(season: Season): Season {
    seasons.push(season);
    return season;
  },

  replaceSeasonById(id: string, season: Season): Season | undefined {
    const index = seasons.findIndex(s => s.id === id);
    if (index === -1) {
      return undefined;
    }
    seasons[index] = season;
    return season;
  },

  removeSeasonById(id: string): void {
    const index = seasons.findIndex(s => s.id === id);
    if (index !== -1) {
      seasons.splice(index, 1);
    }
  },

  getBookingById(id: string): Booking | undefined {
    return bookings.find(b => b.id === id);
  },

  searchBookings(filters: Criteria | LegacyBookingFilters = {}): PagedResponse<Booking> {
    if (isCriteria(filters)) {
      const result = applyCriteria(bookings, filters, resolveBookingField);
      return paginate(result, filters.page, filters.size, !!filters.sorts?.length);
    }

    let result = [...bookings];

    if (filters.rentalId) {
      result = result.filter(b => b.rentalId === filters.rentalId);
    }

    if (filters.statuses?.length) {
      result = result.filter(b => filters.statuses!.includes(b.bookingStatus));
    }

    if (filters.bookingStatus) {
      result = result.filter(b => b.bookingStatus === filters.bookingStatus);
    }

    if (filters.searchValue) {
      const s = filters.searchValue.toLowerCase();
      result = result.filter(
        b =>
          (b.rentalTitle?.toLowerCase().includes(s) ?? false) ||
          b.externalCodeNumber?.toString().includes(s) ||
          (b.mainGuest?.name?.toLowerCase().includes(s) ?? false) ||
          (b.mainGuest?.lastName?.toLowerCase().includes(s) ?? false) ||
          (b.mainGuest?.surname?.toLowerCase().includes(s) ?? false) ||
          b.bookingHolder?.name?.toLowerCase().includes(s)
      );
    }

    if (filters.checkInDate) {
      const from = new Date(filters.checkInDate);
      result = result.filter(b => new Date(b.time?.checkInDate ?? '') >= from);
    }

    if (filters.checkOutDate) {
      const to = new Date(filters.checkOutDate);
      result = result.filter(b => new Date(b.time?.checkOutDate ?? '') <= to);
    }

    return paginate(result, filters.page, filters.size);
  },

  getAllBookings(page = 0, size = 20): PagedResponse<Booking> {
    return paginate(bookings, page, size);
  },

  getBookingsByRentalId(rentalId: string): Booking[] {
    return bookings.filter(b => b.rentalId === rentalId);
  },

  getBookingsByHostId(): Booking[] {
    return bookings;
  },

  getGuestById(id: number): Guest | undefined {
    return guests.find(g => g.id === id);
  },

  getGuestsByBookingId(_bookingId: string): Guest[] {
    return guests;
  },

  saveGuestsByBookingId(_bookingId: string, nextGuests: Guest[]): Guest[] {
    guests.splice(0, guests.length, ...nextGuests.map(guest => ({ ...guest })));
    return guests;
  },

  getAllGuests(): Guest[] {
    return guests;
  },

  getCountries(): Country[] {
    return countries;
  },

  getProvinces(_countryId?: string): Province[] {
    return provinces;
  },

  getCities(provinceCode?: string): City[] {
    if (provinceCode) {
      return cities.filter(c => c.provinceCode === provinceCode);
    }
    return cities;
  },

  getCityById(cityId: string): City | undefined {
    return cities.find(c => c.id === Number(cityId));
  },

  getLocations(): Location[] {
    return locations;
  },

  getLocationById(id: string): Location | undefined {
    return locations.find(l => l.id === id);
  },

  getSpaceTypes(): SpaceType[] {
    return spaceTypes;
  },

  getSpaces(): Space[] {
    return spaces;
  },

  getSpacesByRentalId(rentalId: string): Space[] {
    return spacesByRentalIdMap[rentalId] ?? spacesR0;
  },

  getSpaceById(id: string): Space | undefined {
    const all = [...spacesR0, ...spacesR1, ...spacesGeneric];
    return all.find(s => s.id === id);
  },

  getAmenityCategories(): AmenityCategory[] {
    return amenityCategories;
  },

  getAmenityById(id: string): Amenity | undefined {
    for (const cat of amenityCategories) {
      const found = cat.data.find(a => a.id === id);
      if (found) {
        return found;
      }
    }
    return undefined;
  },

  getLockDevices(rentalId?: string): LockDevice[] {
    if (rentalId === 'rental-mojacar-002') {
      return lockDevicesCantal;
    }
    if (rentalId === 'rental-mojacar-003') {
      return lockDevicesVillazar;
    }
    if (rentalId === 'rental-mojacar-004') {
      return lockDevicesFuente;
    }
    return lockDevices;
  },

  getLockDeviceById(id: string): LockDevice | undefined {
    return [
      ...lockDevices,
      ...lockDevicesCantal,
      ...lockDevicesVillazar,
      ...lockDevicesFuente,
    ].find(d => d.deviceId === id);
  },

  getAccessCodes(): LockAccessCode[] {
    return accessCodes;
  },

  getAccessCodesResponse(): LockAccessCodesResponse {
    return { codes: accessCodes };
  },

  getAccessHistory(): LockAccessHistory[] {
    return accessHistory;
  },

  getPagedAccessHistory(page = 0, size = 20): PagedResponse<LockAccessHistory> {
    return paginate(accessHistory, page, size);
  },

  getLockBattery(rentalId?: string): LockBattery {
    const devicesMap: Record<string, LockDevice[]> = {
      'rental-mojacar-002': lockDevicesCantal,
      'rental-mojacar-003': lockDevicesVillazar,
      'rental-mojacar-004': lockDevicesFuente,
    };
    const devices: LockDevice[] = rentalId ? (devicesMap[rentalId] ?? lockDevices) : lockDevices;
    return buildLockBattery(devices[0]);
  },

  getLockBridgeStatus(): LockStatus {
    return { state: 'CONNECTED' as LockState, error: '' };
  },

  getLockActionResponse(rentalId?: string): LockActionResponse {
    const devicesMap: Record<string, LockDevice[]> = {
      'rental-mojacar-002': lockDevicesCantal,
      'rental-mojacar-003': lockDevicesVillazar,
      'rental-mojacar-004': lockDevicesFuente,
    };
    const devices: LockDevice[] = rentalId ? (devicesMap[rentalId] ?? lockDevices) : lockDevices;
    return buildLockActionResponse(devices[0]);
  },

  getLockConnectionStatus(): { state: string; error: string | null } {
    return { state: 'CONNECTED', error: null };
  },

  getActivityOverview(): MetricResponse {
    return buildActivityOverviewFromStats(_casaMarStats);
  },

  getAccessMetrics(): MetricResponse {
    return buildAccessMetrics();
  },

  getAccessMetricsOverallView(): MetricResponse {
    return buildAccessMetricsOverallView();
  },

  getPerformanceMetrics(): MetricResponse {
    return buildPerformanceMetricsFromStats(_casaMarStats);
  },

  paginate,
};
